(() => {
  'use strict';

  class MtmProduct {
    constructor(section) {
      this.section = section;
      this.id = section.dataset.sectionId;
      this.productUrl = section.dataset.url;

      const jsonEl = section.querySelector('[data-mtm-product-json]');
      this.variants = JSON.parse(jsonEl.textContent);

      this.root = section.querySelector('[data-mtm-root]');
      this.form = section.querySelector('[data-mtm-form]');
      this.idSelect = section.querySelector('[data-mtm-id-select]');
      this.optionGroups = [...section.querySelectorAll('[data-mtm-option-group]')];
      this.skuEl = section.querySelector('[data-mtm-sku]');
      this.availabilityEl = section.querySelector('[data-mtm-availability]');
      this.priceBox = section.querySelector('[data-mtm-price]');
      this.errorEl = section.querySelector('[data-mtm-error]');
      this.addBtn = section.querySelector('[data-mtm-add]');
      this.addBtnText = section.querySelector('[data-mtm-add-text]');
      this.spinner = section.querySelector('[data-mtm-spinner]');
      this.qtyInput = section.querySelector('[data-mtm-qty]');

      this.mediaWrap = section.querySelector('[data-mtm-gallery]');
      this.media = [...section.querySelectorAll('[data-mtm-media]')];
      this.thumbs = [...section.querySelectorAll('[data-mtm-thumb]')];

      this.currentVariant = this.variants.find(
        (v) => String(v.id) === this.selectedIdFromFormOrUrl()
      ) || this.variants.find((v) => v.available);

      this.bindEvents();
      this.applyVariant(this.currentVariant, false);
    }

    /* ---------------- Selection state ---------------- */

    selectedIdFromFormOrUrl() {
      const urlId = new URLSearchParams(window.location.search).get('variant');
      if (urlId) return urlId;
      return this.idSelect ? this.idSelect.value : '';
    }

    selectedOptionValues() {
      return this.optionGroups.map((group) => {
        const checked = group.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    findVariant(values) {
      return this.variants.find((v) =>
        v.options.every((opt, i) => values[i] === null || opt === values[i])
      );
    }

    /* ---------------- DOM updates ---------------- */

    applyVariant(variant, updateUrl = true) {
      if (!variant) return;
      this.currentVariant = variant;

      if (this.idSelect) this.idSelect.value = variant.id;

      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }

      this.updatePrice(variant);
      this.updateSku(variant);
      this.updateAvailability(variant);
      this.updateOptionStates();
      this.updateMedia(variant);
      this.updateBuyButton(variant);
    }

    updatePrice(variant) {
      if (!this.priceBox) return;
      const priceEl = this.priceBox.querySelector('[data-mtm-price-current]');
      const compareEl = this.priceBox.querySelector('[data-mtm-price-compare]');
      const badgeEl = this.priceBox.querySelector('[data-mtm-savings]');

      const format = (cents) => {
        const amount = cents / 100;
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'en', {
            style: 'currency',
            currency: window.Shopify?.currency?.active || 'AED',
            minimumFractionDigits: 2,
          }).format(amount);
        } catch {
          return `${amount.toFixed(2)} AED`;
        }
      };

      if (priceEl) priceEl.textContent = format(variant.price);
      if (compareEl) {
        if (variant.compare_at_price > variant.price) {
          compareEl.textContent = format(variant.compare_at_price);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }
      if (badgeEl) {
        if (variant.compare_at_price > variant.price) {
          const pct = Math.round(
            ((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100
          );
          badgeEl.textContent = `Save ${pct}%`;
          badgeEl.hidden = false;
        } else {
          badgeEl.hidden = true;
        }
      }
    }

    updateSku(variant) {
      if (this.skuEl) this.skuEl.textContent = variant.sku || '—';
    }

    updateAvailability(variant) {
      if (!this.availabilityElSafe()) return;
      const el = this.availabilityEl;
      el.classList.toggle('is-sold-out', !variant.available);
      if (!variant.available) {
        el.textContent = 'Sold out';
      } else if (
        variant.inventory_management === 'shopify' &&
        variant.inventory_quantity > 0 &&
        variant.inventory_quantity <= 10
      ) {
        el.textContent = `Low stock — only ${variant.inventory_quantity} left`;
      } else {
        el.textContent = 'In stock';
      }
    }

    availabilityElSafe() {
      if (!this.availabilityEl) {
        this.availabilityEl = this.section.querySelector('[data-mtm-availability]');
      }
      return !!this.availabilityEl;
    }

    updateBuyButton(variant) {
      if (!this.addBtn) return;
      this.addBtn.disabled = !variant.available;
      if (this.addBtnText) {
        this.addBtnText.textContent = variant.available ? 'Add to cart' : 'Sold out';
      }
    }

    /* Enable/disable pills per available combinations */
    updateOptionStates() {
      const values = this.selectedOptionValues();

      this.optionGroups.forEach((group, index) => {
        group.querySelectorAll('[data-mtm-option-label]').forEach((label) => {
          const testValues = [...values];
          testValues[index] = label.dataset.value;
          const match = this.findVariant(testValues);
          const available = !!match && match.available;
          const exists = !!match;
          label.classList.toggle('is-unavailable', exists && !available);
          label.classList.toggle('is-nonexistent', !exists);
        });
      });
    }

    /* ---------------- Media ---------------- */

    updateMedia(variant) {
      if (!variant.featured_media) return;
      this.setMedia(variant.featured_media.id);
    }

    setMedia(mediaId) {
      this.media.forEach((m) =>
        m.classList.toggle('is-active', String(m.dataset.mediaId) === String(mediaId))
      );
      this.thumbs.forEach((t) =>
        t.classList.toggle('is-active', String(t.dataset.mediaId) === String(mediaId))
      );
    }

    /* ---------------- Events ---------------- */

    bindEvents() {
      this.section.addEventListener('change', (e) => {
        if (e.target.matches('[data-mtm-option-input]')) {
          const variant = this.findVariant(this.selectedOptionValues());
          if (variant) this.applyVariant(variant);
          else this.updateOptionStates();
        }
        if (e.target === this.idSelect) {
          const variant = this.variants.find((v) => String(v.id) === e.target.value);
          if (variant) this.applyVariant(variant, false);
        }
      });

      this.thumbs.forEach((thumb) =>
        thumb.addEventListener('click', () => this.setMedia(thumb.dataset.mediaId))
      );

      this.section.querySelectorAll('[data-mtm-qty-step]').forEach((btn) =>
        btn.addEventListener('click', () => {
          if (!this.qtyInput) return;
          this.qtyInput.value = Math.max(
            1,
            (parseInt(this.qtyInput.value, 10) || 1) + parseInt(btn.dataset.mtmQtyStep, 10)
          );
        })
      );

      this.form?.addEventListener('submit', (e) => this.onAddToCart(e));
    }

    async onAddToCart(e) {
      e.preventDefault();

      if (!this.currentVariant?.available) return;
      this.setLoading(true);
      this.hideError();

      const qty = Math.max(1, parseInt(this.qtyInput?.value, 10) || 1);

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: this.currentVariant.id,
            quantity: qty,
            sections_url: window.location.href,
          }),
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || err.message || 'Unable to add to cart.');
        }

        const item = await res.json();
        this.dispatchCartRefresh(item);
      } catch (err) {
        this.showError(err.message);
      } finally {
        this.setLoading(false);
      }
    }

    dispatchCartRefresh(item) {
      /* Standard Stiletto pattern: refresh sections + open drawer */
      document.dispatchEvent(
        new CustomEvent('cart:refresh', { bubbles: true, detail: { item } })
      );
      document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
      window.dispatchEvent(
        new CustomEvent('mtm:added', { detail: { productId: this.section.dataset.productId, item } })
      );
    }

    setLoading(loading) {
      if (this.addBtn) this.addBtn.disabled = loading || !this.currentVariant?.available;
      if (this.spinner) this.spinner.hidden = !loading;
_LEAKCHECK    }

    showError(msg) {
      if (!this.errorEl) return;
      this.errorEl.textContent = msg;
      this.errorEl.hidden = false;
    }

    hideError() {
      if (this.errorEl) this.errorEl.hidden = true;
    }
  }

  const init = () => {
    document.querySelectorAll('.mtm-product').forEach((s) => new MtmProduct(s));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();